/**
 * Cart-invite bootstrap — global, pre-auth, runs on every storefront page.
 *
 * Two jobs, both of which have to happen before anything else on the page can
 * touch the URL:
 *
 * 1. `/#cart-invite=<token>` — an editable saved-cart invite. The token is
 *    captured into TAB-SCOPED sessionStorage, stripped from the address bar,
 *    and the browser is sent to /account, where the authenticated runtime
 *    exchanges it exactly once. The link points at the storefront ROOT rather
 *    than /account because Shopify and the OTP app do not preserve a fragment
 *    through login (plan §6) — the root always renders, so the fragment is
 *    always readable before any redirect can eat it.
 *
 * 2. `?sharedCart=` — a share link from the customers-b-b app this page
 *    replaces. Those tokens mean nothing to the new system (no migration, by
 *    decision #8), so the shim strips the parameter and lands the customer on
 *    the saved-carts tab with an honest explanation instead of a dead page.
 *
 * An invite token is a bearer credential. It only ever lives in a fragment,
 * which browsers do not send to servers, do not put in a Referer, and do not
 * write to an access log. Nothing here may move one into a query string, a
 * fetch, a console line, or an analytics payload — including on the failure
 * paths, which is why every branch below either keeps it in the fragment or
 * puts it in sessionStorage, and never anywhere else.
 *
 * Loaded WITHOUT defer, high in <head>, deliberately: a deferred copy runs
 * after app scripts that may redirect, and a fragment lost to a redirect cannot
 * be recovered.
 */
(function(window, document) {
  'use strict';

  if (window.IBCartInviteBootstrapRan) return;
  window.IBCartInviteBootstrapRan = true;

  var INVITE_KEY = 'ib:cart-invite';
  var LEGACY_KEY = 'ib:legacy-shared-cart';
  var FRAGMENT_PREFIX = 'cart-invite=';

  var script = document.currentScript;
  var accountUrl = (script && script.getAttribute('data-account-url')) || '/account';

  function store(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch (error) {
      // Private-mode Safari and storage-partitioned embeds both throw here.
      return false;
    }
  }

  function onAccountPage() {
    var path = window.location.pathname || '';
    return path === accountUrl || path.indexOf(accountUrl + '/') === 0;
  }

  function replaceUrl(url) {
    if (window.history && window.history.replaceState) {
      try {
        window.history.replaceState(null, '', url);
        return true;
      } catch (error) {
        return false;
      }
    }
    return false;
  }

  /* ------------------------------------------------------------- the invite */

  function readInviteToken() {
    var hash = window.location.hash || '';
    if (hash.charAt(0) === '#') hash = hash.slice(1);
    if (hash.indexOf(FRAGMENT_PREFIX) !== 0) return '';

    var raw = hash.slice(FRAGMENT_PREFIX.length);
    if (!raw) return '';

    try {
      return decodeURIComponent(raw);
    } catch (error) {
      return raw;
    }
  }

  function handleInvite() {
    var token = readInviteToken();
    if (!token) return false;

    // Same-page handoff, so the account runtime can still redeem the invite in
    // the one case sessionStorage is unavailable AND we are already here.
    window.IBCartInvite = { token: token };

    var stored = store(INVITE_KEY, token);
    var here = onAccountPage();

    if (stored) {
      // The token is safe in storage, so the address bar can be cleaned
      // unconditionally — including before a navigation, so a token never
      // survives in history.
      replaceUrl(window.location.pathname + window.location.search);
      if (!here) window.location.replace(accountUrl + '#saved-carts');
      return true;
    }

    // No storage. The token cannot survive a login round trip, so the only
    // honest option is to carry it in the fragment to the account page and let
    // the runtime try; if login intervenes the customer sees the same
    // invalid-link copy as any other unusable invite, which is the truth from
    // where they are standing.
    if (!here) {
      window.location.replace(accountUrl + '#' + FRAGMENT_PREFIX + encodeURIComponent(token));
    }
    return true;
  }

  /* ------------------------------------------------- the legacy cbb shim */

  function handleLegacyShare() {
    var search = window.location.search || '';
    if (search.indexOf('sharedCart=') === -1) return false;

    var params;
    try {
      params = new window.URLSearchParams(search);
    } catch (error) {
      return false;
    }
    if (!params.has('sharedCart')) return false;

    // The value is never read, kept, or sent anywhere: it addresses a system
    // that is being switched off, and there is nothing on this side to look it
    // up in. Only the fact that an old link was followed is carried forward.
    params.delete('sharedCart');
    store(LEGACY_KEY, '1');

    var rest = params.toString();
    var cleaned = window.location.pathname + (rest ? '?' + rest : '');

    if (onAccountPage()) {
      replaceUrl(cleaned + '#saved-carts');
      return true;
    }

    replaceUrl(cleaned);
    window.location.replace(accountUrl + '#saved-carts');
    return true;
  }

  try {
    if (!handleInvite()) handleLegacyShare();
  } catch (error) {
    // A throw here would take the whole page's <head> with it. A share link
    // that silently fails to redeem is recoverable; a storefront that does not
    // render is not.
  }
})(window, document);
